import React, { useState } from 'react';
import { Search, Filter, MoreVertical, Phone, Mail, MapPin, UserPlus } from 'lucide-react';
import EmptyState from '../EmptyState';
import type { Customer } from '../../types/customer';

interface CustomerListProps {
  customers: Customer[];
  onSelect: (customer: Customer | null) => void;
  isLoading?: boolean;
}

const CustomerList: React.FC<CustomerListProps> = ({ 
  customers, 
  onSelect,
  isLoading = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = 
      customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.document.includes(searchTerm) ||
      (customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
      (customer.phone?.includes(searchTerm) || false);

    const matchesStatus = 
      statusFilter === 'all' || customer.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'blocked':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-lg border border-gray-100 p-8">
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (customers.length === 0) {
    return (
      <EmptyState
        title="Nenhum cliente cadastrado"
        description="Comece cadastrando seu primeiro cliente para gerenciar vendas e relacionamentos."
        action={
          <button
            onClick={() => onSelect(null)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <UserPlus className="h-5 w-5 mr-2" />
            Cadastrar Primeiro Cliente
          </button>
        }
      />
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-100">
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Buscar por nome, documento, email ou telefone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Todos os Status</option>
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
              <option value="blocked">Bloqueados</option>
            </select>
            <button className="p-2 hover:bg-gray-100 rounded-md">
              <Filter className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      <div className="divide-y divide-gray-200">
        {filteredCustomers.map((customer) => (
          <div
            key={customer.id}
            className="p-4 hover:bg-gray-50 cursor-pointer"
            onClick={() => onSelect(customer)}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    {customer.name}
                  </h3>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(customer.status)}`}>
                    {customer.status === 'active' ? 'Ativo' :
                     customer.status === 'inactive' ? 'Inativo' : 'Bloqueado'}
                  </span>
                </div>
                
                <div className="mt-1 text-sm text-gray-500">
                  {customer.type === 'PF' ? 'CPF' : 'CNPJ'}: {customer.document}
                </div>
                
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="flex items-center text-sm text-gray-500">
                    <Phone className="h-4 w-4 mr-1" />
                    {customer.phone || 'Não informado'}
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <Mail className="h-4 w-4 mr-1" />
                    {customer.email || 'Não informado'}
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <MapPin className="h-4 w-4 mr-1" />
                    {customer.address_city && customer.address_state 
                      ? `${customer.address_city}, ${customer.address_state}`
                      : 'Endereço não informado'}
                  </div>
                </div>
              </div>
              
              <button 
                className="p-2 hover:bg-gray-100 rounded-full"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(customer);
                }}
              >
                <MoreVertical className="h-5 w-5 text-gray-400" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CustomerList;